import { useEffect, useMemo, useState } from "react";
import { Download, Package } from "@/components/icons";
import {
  Button,
  Checkbox,
  DatePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
} from "@/components/kit";
import { cn } from "@/lib/utils";
import { isMatchedImportLine } from "@/modules/inventory/domain/import-order-line-filters";
import type { ReceiveImportOrderDTO } from "@/modules/inventory/domain/import-order-contracts";
import type { ImportOrderPhase13DetailDTO } from "@/modules/inventory/domain/types";
import {
  convertReceiveQtyToStock,
  formatQtyWithUnit,
  usesAlternateReceiveUnit,
} from "@/modules/products/domain/unit-conversion";
import { formatDateForInput } from "@/shared/lib/date-time";
import { usePreviewImportOrderLandedCost } from "../api/import-orders-queries";
import { formatLakAmount, importOrderPriceUnitLabel } from "../lib/import-order-labels";

type ReceiveRow = {
  importOrderLineId: string;
  productName: string;
  variantName: string;
  remaining: number;
  unitPriceForeign: number;
  enabled: boolean;
  quantity: number;
  stockUnitLabel: string;
  receiveUnitLabel: string | null;
  receiveConversionFactor: number;
};

type ReceiveImportOrderDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: ImportOrderPhase13DetailDTO;
  isSubmitting?: boolean;
  onConfirm: (input: ReceiveImportOrderDTO) => void | Promise<void>;
};

function buildRows(order: ImportOrderPhase13DetailDTO): ReceiveRow[] {
  return (order.lines ?? [])
    .filter(
      (line) => isMatchedImportLine(line) && line.remainingQuantity > 0,
    )
    .map((line) => ({
      importOrderLineId: line.id,
      productName: line.productName,
      variantName: line.variantName,
      remaining: line.remainingQuantity,
      unitPriceForeign: Number(line.unitPriceForeign),
      enabled: true,
      quantity: line.remainingQuantity,
      stockUnitLabel: line.stockUnitLabel,
      receiveUnitLabel: line.receiveUnitLabel,
      receiveConversionFactor: line.receiveConversionFactor,
    }));
}

export function ReceiveImportOrderDialog({
  open,
  onOpenChange,
  order,
  isSubmitting,
  onConfirm,
}: ReceiveImportOrderDialogProps) {
  const [rows, setRows] = useState<ReceiveRow[]>([]);
  const [receivedAt, setReceivedAt] = useState(() =>
    formatDateForInput(new Date()),
  );
  const [exchangeRate, setExchangeRate] = useState(0);
  const [shippingTotalLak, setShippingTotalLak] = useState(0);
  const [trackingNo, setTrackingNo] = useState("");
  const [carrierName, setCarrierName] = useState("");
  const previewMutation = usePreviewImportOrderLandedCost();

  useEffect(() => {
    if (!open || !order) return;
    setRows(buildRows(order));
    setReceivedAt(formatDateForInput(new Date()));
    setExchangeRate(Number(order.exchangeRate));
    setShippingTotalLak(Number(order.shippingTotalLak));
    setTrackingNo(order.trackings[0]?.trackingNo ?? "");
    setCarrierName(order.trackings[0]?.carrierName ?? "");
  }, [open, order]);

  const isLakCurrency = order.currency === "LAK";
  const priceUnit = importOrderPriceUnitLabel(order.currency);
  const effectiveExchangeRate = isLakCurrency ? 1 : exchangeRate;

  const previewInput = useMemo(
    () => ({
      currency: order?.currency === "LAK" ? ("LAK" as const) : ("CNY" as const),
      exchangeRate: effectiveExchangeRate,
      shippingTotalLak,
      lines: (order?.lines ?? [])
        .filter(isMatchedImportLine)
        .map((line) => {
        const row = rows.find((r) => r.importOrderLineId === line.id);
        return {
          variantId: line.variantId!,
          productName: line.productName,
          variantName: line.variantName,
          orderedQuantity: line.orderedQuantity,
          unitPriceForeign:
            row?.unitPriceForeign ?? Number(line.unitPriceForeign),
        };
      }),
    }),
    [effectiveExchangeRate, order?.currency, order?.lines, rows, shippingTotalLak],
  );

  useEffect(() => {
    if (!open || rows.length === 0) return;
    const timer = setTimeout(() => {
      previewMutation.mutate(previewInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [open, previewInput, rows.length]);

  const selectedTotal = useMemo(
    () =>
      rows.reduce(
        (sum, row) => (row.enabled ? sum + Math.max(0, row.quantity) : sum),
        0,
      ),
    [rows],
  );

  const selectedStockTotal = useMemo(
    () =>
      rows.reduce((sum, row) => {
        if (!row.enabled || row.quantity <= 0) return sum;
        return (
          sum +
          convertReceiveQtyToStock(row.quantity, row.receiveConversionFactor)
        );
      }, 0),
    [rows],
  );

  const selectedReceiveUnit = useMemo(() => {
    const enabled = rows.filter((row) => row.enabled && row.quantity > 0);
    if (enabled.length === 0) return null;
    const first =
      enabled[0].receiveUnitLabel?.trim() || enabled[0].stockUnitLabel;
    return enabled.every(
      (row) => (row.receiveUnitLabel?.trim() || row.stockUnitLabel) === first,
    )
      ? first
      : null;
  }, [rows]);

  const preview = previewMutation.data;
  const pendingLineCount = (order?.lines ?? []).filter(
    (line) => line.matchStatus === "pending",
  ).length;
  const previewLandedForReceive = useMemo(() => {
    if (!preview?.lines || !order) return 0;
    return rows.reduce((sum, row) => {
      if (!row.enabled || row.quantity <= 0) return sum;
      const line = order.lines.find((l) => l.id === row.importOrderLineId);
      const previewLine = preview.lines.find(
        (l) => l.variantId === line?.variantId,
      );
      const unit =
        previewLine?.unitLandedCostLak ?? Number(line?.unitLandedCostLak ?? 0);
      return sum + unit * row.quantity;
    }, 0);
  }, [order, preview, rows]);

  const canSubmit =
    Boolean(order) &&
    receivedAt.length > 0 &&
    (isLakCurrency || exchangeRate > 0) &&
    shippingTotalLak >= 0 &&
    selectedTotal > 0 &&
    rows.every(
      (row) =>
        !row.enabled ||
        (row.quantity > 0 &&
          row.quantity <= row.remaining &&
          row.unitPriceForeign >= 0),
    );

  function toggleLine(lineId: string, enabled: boolean) {
    setRows((prev) =>
      prev.map((row) =>
        row.importOrderLineId === lineId
          ? { ...row, enabled, quantity: enabled ? row.remaining : 0 }
          : row,
      ),
    );
  }

  function setQuantity(lineId: string, quantity: number) {
    setRows((prev) =>
      prev.map((row) => {
        if (row.importOrderLineId !== lineId) return row;
        const qty = Math.max(0, Math.min(quantity, row.remaining));
        return { ...row, quantity: qty, enabled: qty > 0 };
      }),
    );
  }

  function setLinePrice(lineId: string, unitPriceForeign: number) {
    setRows((prev) =>
      prev.map((row) =>
        row.importOrderLineId === lineId
          ? { ...row, unitPriceForeign: Math.max(0, unitPriceForeign) }
          : row,
      ),
    );
  }

  async function handleConfirm() {
    if (!canSubmit || !order) return;
    await onConfirm({
      receivedAt: new Date(receivedAt).toISOString(),
      exchangeRate: effectiveExchangeRate,
      shippingTotalLak,
      trackingNo: trackingNo.trim() || null,
      carrierName: carrierName.trim() || null,
      linePrices: (order.lines ?? [])
        .filter(isMatchedImportLine)
        .map((line) => {
        const row = rows.find((r) => r.importOrderLineId === line.id);
        return {
          importOrderLineId: line.id,
          unitPriceForeign:
            row?.unitPriceForeign ?? Number(line.unitPriceForeign),
        };
      }),
      lines: rows
        .filter((row) => row.enabled && row.quantity > 0)
        .map((row) => ({
          importOrderLineId: row.importOrderLineId,
          receivedQuantity: row.quantity,
        })),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90dvh,720px)] flex-col gap-4 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            ສ້າງໃບຮັບເຂົ້າສະຕັອກ
          </DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
          <div className="rounded-xl border border-secondary/30 bg-secondary/10 px-3 py-2.5 text-sm">
            <p className="font-medium">
              {isLakCurrency
                ? `ລາຄາ (${priceUnit}) — ບໍ່ຄຳນວນອັດຕາແລກປ່ຽນ`
                : `ອັດຕາແລກປ່ຽນ (CNY→LAK) / ລາຄາ (${priceUnit})`}
            </p>
            <p className="mt-1 text-muted-foreground text-xs">
              ສ້າງໃບຮັບເຂົ້າແລ້ວ — ກະລຸນາຢືນຢັນເພື່ອເພີ່ມສະຕັອກ
            </p>
          </div>

          {pendingLineCount > 0 ? (
            <div className="rounded-xl border border-amber-500/25 bg-amber-500/8 px-3 py-2.5 text-amber-950 text-sm dark:text-amber-100">
              {pendingLineCount} ລາຍການຍັງບໍ່ຈັບຄູ່ SKU — ຈະບໍ່ຖືກຮັບໃນຂັ້ນຕອນນີ້
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label>ວັນທີຮັບເຂົ້າ</Label>
            <DatePicker
              value={receivedAt ? new Date(receivedAt) : null}
              onChange={(date) =>
                setReceivedAt(date ? formatDateForInput(date) : "")
              }
              className="w-full"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {!isLakCurrency ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="receive-exchange-rate">ອັດຕາແລກປ່ຽນ</Label>
                <Input
                  id="receive-exchange-rate"
                  type="number"
                  min={0}
                  step="0.0001"
                  value={exchangeRate || ""}
                  onChange={(e) =>
                    setExchangeRate(Math.max(0, Number(e.target.value) || 0))
                  }
                />
              </div>
            ) : null}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="receive-shipping-total">ຄ່າຂົນສົ່ງລວມ (LAK)</Label>
              <Input
                id="receive-shipping-total"
                type="number"
                min={0}
                value={shippingTotalLak || ""}
                onChange={(e) =>
                  setShippingTotalLak(Math.max(0, Number(e.target.value) || 0))
                }
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="receive-tracking-no">Tracking</Label>
              <Input
                id="receive-tracking-no"
                value={trackingNo}
                onChange={(e) => setTrackingNo(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="receive-carrier-name">ຊື່ຂົນສົ່ງ</Label>
              <Input
                id="receive-carrier-name"
                value={carrierName}
                onChange={(e) => setCarrierName(e.target.value)}
              />
            </div>
          </div>

          <ul className="flex flex-col gap-2">
            {rows.length === 0 ? (
              <li className="rounded-xl border border-dashed px-3 py-6 text-center text-muted-foreground text-sm">
                ບໍ່ມີລາຍການທີ່ຮັບໄດ້ — ຈັບຄູ່ SKU ກ່ອນ ຫຼື ລໍຖ້າຂອງມາຮອດ
              </li>
            ) : (
              rows.map((row) => {
              const hasAltUnit = usesAlternateReceiveUnit(row);
              const receiveUnit =
                row.receiveUnitLabel?.trim() || row.stockUnitLabel;
              const stockQty = convertReceiveQtyToStock(
                row.quantity,
                row.receiveConversionFactor,
              );
              const remainingStockQty = convertReceiveQtyToStock(
                row.remaining,
                row.receiveConversionFactor,
              );

              return (
                <li
                  key={row.importOrderLineId}
                  className={cn(
                    "rounded-xl border p-3",
                    row.enabled ? "border-secondary/30" : "opacity-70",
                  )}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={row.enabled}
                      onCheckedChange={(c) =>
                        toggleLine(row.importOrderLineId, Boolean(c))
                      }
                      className="mt-1"
                    />
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-secondary">
                      <Package className="size-4" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-2">
                      <div>
                        <p className="font-medium text-sm">{row.productName}</p>
                        <p className="text-muted-foreground text-xs">
                          {row.variantName} · ຄ້າງ{" "}
                          {formatQtyWithUnit(row.remaining, receiveUnit)}
                          {hasAltUnit
                            ? ` (→ ${formatQtyWithUnit(remainingStockQty, row.stockUnitLabel)})`
                            : ""}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <div className="flex flex-col gap-1">
                          <Label className="text-muted-foreground text-xs">
                            ຈຳນວນຮັບ ({receiveUnit})
                          </Label>
                          <Input
                            type="number"
                            min={0}
                            max={row.remaining}
                            disabled={!row.enabled}
                            value={row.enabled ? row.quantity : 0}
                            onChange={(e) =>
                              setQuantity(
                                row.importOrderLineId,
                                Number(e.target.value) || 0,
                              )
                            }
                            className="h-9 w-24 tabular-nums"
                          />
                          {hasAltUnit && row.enabled && row.quantity > 0 ? (
                            <p className="text-muted-foreground text-xs">
                              → {formatQtyWithUnit(stockQty, row.stockUnitLabel)}{" "}
                              ເຂົ້າສະຕັອກ
                            </p>
                          ) : null}
                        </div>
                        <div className="flex flex-col gap-1">
                          <Label className="text-muted-foreground text-xs">
                            ລາຄາ/{receiveUnit} ({priceUnit})
                          </Label>
                          <Input
                            type="number"
                            min={0}
                            step="0.01"
                            disabled={!row.enabled}
                            value={row.unitPriceForeign || ""}
                            onChange={(e) =>
                              setLinePrice(
                                row.importOrderLineId,
                                Number(e.target.value) || 0,
                              )
                            }
                            className="h-9 w-28 tabular-nums"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })
            )}
          </ul>

          {previewMutation.isPending ? (
            <Skeleton className="h-20 rounded-xl" />
          ) : preview ? (
            <div className="grid gap-2 rounded-xl border border-secondary/20 bg-secondary/5 p-3 text-sm sm:grid-cols-3">
              <div>
                <p className="text-muted-foreground text-xs">ມູນຄ່າສິນຄ້າ</p>
                <p className="font-medium tabular-nums">
                  {formatLakAmount(preview.goodsSubtotalLak)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">ຂົນສົ່ງ</p>
                <p className="font-medium tabular-nums">
                  {formatLakAmount(preview.shippingTotalLak)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Landed ທີ່ຈະຮັບ</p>
                <p className="font-semibold tabular-nums">
                  {formatLakAmount(previewLandedForReceive)}
                </p>
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <p className="text-muted-foreground text-sm">
            ຈະຮັບ {selectedTotal}
            {selectedReceiveUnit ? ` ${selectedReceiveUnit}` : ""}
            {rows.some(
              (row) =>
                row.enabled &&
                row.quantity > 0 &&
                usesAlternateReceiveUnit(row),
            )
              ? ` → ${selectedStockTotal.toLocaleString("lo-LA")} ເຂົ້າສະຕັອກ`
              : ""}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              className="rounded-full"
              disabled={!canSubmit}
              isLoading={isSubmitting}
              onClick={handleConfirm}
            >
              <Download data-icon="inline-start" />
              ສ້າງໃບຮັບເຂົ້າ
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
